import React, { useState, useEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { AvatarsService } from '../../services/avatarsService';
import { AvatarModel } from '../../types/avatar';
import UploadAvatarDialog from './UploadAvatarDialog';
import DeleteAvatarDialog from './DeleteAvatarDialog';
import ShimmerAvatar from './ShimmerAvatar';

import { Plus } from 'lucide-react';

import { toast } from 'react-toastify';

const Avatars = () => {
  const [avatars, setAvatars] = useState<AvatarModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedAvatar, setSelectedAvatar] = useState<AvatarModel | null>(null);

  useEffect(() => {
    loadAvatars();
  }, []);

  const loadAvatars = async () => {
    try {
      setIsLoading(true);
      const avatarsData = await AvatarsService.loadAvatars();
      setAvatars(avatarsData);
    } catch (error) {
      console.error('Error loading avatars:', error);
      toast.error('Failed to load avatars. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadAvatar = () => {
    setShowUploadDialog(true);
  };

  const handleDeleteAvatar = (avatar: AvatarModel) => {
    setSelectedAvatar(avatar);
    setShowDeleteDialog(true);
  };

  const handleSaveAvatar = async (avatar: AvatarModel) => {
    try {
      await AvatarsService.addAvatar(avatar);
      await loadAvatars();
      toast.success('Avatar uploaded successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving avatar:', error);
      toast.error('Failed to upload avatar. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDeleteAvatarConfirm = async () => {
    if (selectedAvatar) {
      try {
        await AvatarsService.deleteAvatar(selectedAvatar.id);
        await loadAvatars();
        toast.success('Avatar deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('Error deleting avatar:', error);
        toast.error('Failed to delete avatar. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        throw error;
      }
    }
  };

  const handleCloseUploadDialog = () => setShowUploadDialog(false);
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedAvatar(null);
  };

  const getGridColumns = () => {
    if (typeof window === 'undefined') return 4;
    const width = window.innerWidth;
    if (width < 640) return 2; // Mobile
    if (width < 768) return 3; // Tablet
    if (width < 1024) return 4; // Small desktop
    if (width < 1280) return 5; // Medium desktop
    return 6; // Large desktop (max 7 as per Flutter code)
  };

  const gridColumns = getGridColumns();

  const handleImageLoadStart = (avatarId: string) => {
    setLoadingImages(prev => new Set(prev).add(avatarId));
  };

  const handleImageLoadComplete = (avatarId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(avatarId);
      return newSet;
    });
  };

  return (
    <React.Fragment>
      {/* Desktop Breadcrumb */}
      <div className="hidden md:block">
        <BreadCrumb title='Avatars' pageTitle='Dashboard' />
      </div>

      {/* Mobile Header with Breadcrumb and Title */}
      <div className="flex flex-row items-center justify-between gap-4 py-4 md:hidden">
        <h6 className="text-15 font-semibold">Avatars</h6>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-zink-400">Dashboard</span>
          <span className="text-slate-400 dark:text-zink-500">›</span>
          <span className="text-sm text-slate-500 dark:text-zink-400">Avatars</span>
        </div>
      </div>

      <div className="flex justify-end mb-5">
        <button
          onClick={handleUploadAvatar}
          className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
        >
          <Plus className="inline-block size-4 mr-1" /> <span className="align-middle">Upload New Avatar</span>
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : avatars.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-slate-500 dark:text-zink-200">No avatars found</p>
        </div>
      ) : (
        <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}>
          {avatars.map((avatar, index) => (
            <div key={avatar.id} className="flex flex-col items-center">
              <div className="size-24 bg-slate-100 rounded-full dark:bg-zink-600 shrink-0 mb-3 overflow-hidden relative">
                {loadingImages.has(avatar.id) && (
                  <ShimmerAvatar size={96} className="absolute inset-0" />
                )}
                <img
                  src={avatar.photoUrl}
                  alt="Avatar"
                  className={`w-full h-full object-cover ${loadingImages.has(avatar.id) ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                  onLoadStart={() => handleImageLoadStart(avatar.id)}
                  onLoad={() => handleImageLoadComplete(avatar.id)}
                  onError={(e) => {
                    handleImageLoadComplete(avatar.id);
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) nextElement.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gray-100 rounded-full dark:bg-gray-500/20 items-center justify-center hidden absolute inset-0">
                  <span className="text-gray-500 text-2xl">👤</span>
                </div>
              </div>
              <button
                onClick={() => handleDeleteAvatar(avatar)}
                className="text-slate-500 border border-slate-300 hover:text-red-500 hover:border-red-500 dark:text-zink-200 dark:border-zink-500 dark:hover:text-red-500 dark:hover:border-red-500 px-3 py-1 rounded-md text-sm transition-colors"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <UploadAvatarDialog
        onClose={handleCloseUploadDialog}
        onSave={handleSaveAvatar}
        show={showUploadDialog}
      />

      {selectedAvatar && (
        <DeleteAvatarDialog
          avatar={selectedAvatar}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDeleteAvatarConfirm}
          show={showDeleteDialog}
        />
      )}
    </React.Fragment>
  );
};

export default Avatars;